import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Search, X } from 'lucide-react';

/**
 * MemberHierarchySearch Component
 * 
 * Provides a search input that filters organizational hierarchy by member name, email, or position.
 * Returns filtered hierarchy through callback.
 * 
 * @param {Object} props
 * @param {Array} props.users - All users in the organization
 * @param {Array} props.hierarchy - Original hierarchy structure
 * @param {Function} props.onFilteredDataChange - Callback with filtered hierarchy and search term
 * @param {boolean} props.isDark - Theme mode (dark/light)
 * @param {string} props.placeholder - Optional placeholder text
 */
const MemberHierarchySearch = ({
  users = [],
  hierarchy = [],
  onFilteredDataChange,
  isDark = false,
  placeholder = "Search members by name, email, or position..."
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Search input styling classes
  const searchInputClass = `w-full pl-10 pr-10 py-3 rounded-lg border focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/50 transition-all duration-200 ${
    isDark
      ? 'bg-black border-white/10 text-white placeholder-gray-400'
      : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400 shadow-sm'
  }`;

  const searchClearButtonClass = `absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${
    isDark ? 'text-gray-400 hover:text-white' : 'text-slate-500 hover:text-slate-700'
  }`;

  /**
   * Filters users based on search term
   * Matches against: firstName, lastName, email, position
   */
  const filterUsers = useCallback((searchQuery) => {
    if (!searchQuery || searchQuery.trim() === '') {
      return users;
    }

    const query = searchQuery.toLowerCase().trim();
    
    return users.filter((user) => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
      const email = (user.email || '').toLowerCase();
      const position = (user.position || '').toLowerCase();
      
      return (
        fullName.includes(query) ||
        email.includes(query) ||
        position.includes(query)
      );
    });
  }, [users]);

  /**
   * Filters hierarchy structure based on search term
   * Only shows levels/positions that contain matching members
   */
  const filterHierarchy = useCallback((searchQuery, originalHierarchy, allUsers) => {
    if (!searchQuery || searchQuery.trim() === '') {
      return originalHierarchy;
    }

    const matchingUsers = filterUsers(searchQuery);
    const matchingUserIds = new Set(matchingUsers.map(user => String(user._id || user.id)));

    // Filter hierarchy: only keep levels/positions with matching members
    return originalHierarchy
      .map((level) => {
        const filteredPositions = level.positions
          .map((position) => {
            // Get members for this position
            const positionMembers = allUsers.filter((user) => {
              const userPosition = (user.position || '').trim();
              const positionName = (position.roleName || '').trim();
              return userPosition === positionName;
            });

            // Check if any member in this position matches
            const hasMatchingMember = positionMembers.some((member) =>
              matchingUserIds.has(String(member._id || member.id))
            );

            if (hasMatchingMember) {
              return {
                ...position,
                // Optionally filter members within position (but we'll show all for context)
                // members: positionMembers.filter(member => 
                //   matchingUserIds.has(String(member._id || member.id))
                // )
              };
            }
            return null;
          })
          .filter(Boolean); // Remove null positions

        // Only return level if it has matching positions
        if (filteredPositions.length > 0) {
          return {
            ...level,
            positions: filteredPositions,
          };
        }
        return null;
      })
      .filter(Boolean); // Remove null levels
  }, [filterUsers]);

  /**
   * Memoized filtered hierarchy
   */
  const filteredHierarchy = useMemo(() => {
    return filterHierarchy(searchTerm, hierarchy, users);
  }, [searchTerm, hierarchy, users, filterHierarchy]);

  /**
   * Count of matching members
   */
  const matchingMembersCount = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      return users.length;
    }
    return filterUsers(searchTerm).length;
  }, [searchTerm, users, filterUsers]);

  /**
   * Notify parent component of filtered data changes
   * Also triggers on mount to initialize filtered hierarchy
   */
  useEffect(() => {
    if (onFilteredDataChange) {
      onFilteredDataChange({
        filteredHierarchy,
        searchTerm,
        matchingMembersCount,
        hasSearch: searchTerm.trim() !== '',
      });
    }
  }, [filteredHierarchy, searchTerm, matchingMembersCount, onFilteredDataChange]);

  /**
   * Handle search input change
   */
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  /**
   * Clear search
   */
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="mb-6">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleSearchChange}
          className={searchInputClass}
        />
        {searchTerm && (
          <button
            onClick={handleClearSearch}
            className={searchClearButtonClass}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Search Results Count */}
      {searchTerm && (
        <div className={`mt-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          {matchingMembersCount > 0 ? (
            <span>
              Found <span className="font-semibold">{matchingMembersCount}</span> member{matchingMembersCount !== 1 ? 's' : ''} matching "{searchTerm}"
            </span>
          ) : (
            <span className="text-red-400">
              No members found matching "{searchTerm}"
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default MemberHierarchySearch;

